import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FeeContext = createContext();

export const FeeProvider = ({ children }) => {
  const [feeRates, setFeeRates] = useState({
    client_fee_rate: 10, // Default 10%
    provider_fee_rate: 5, // Default 5%
    total_platform_rate: 15,
    loaded: false
  });

  const refreshFeeRates = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/fee-rates`);
      setFeeRates({
        ...response.data,
        loaded: true
      });
    } catch (error) {
      console.error('Failed to fetch fee rates, using defaults:', error);
      setFeeRates(prev => ({ ...prev, loaded: true }));
    }
  }, []);

  useEffect(() => {
    refreshFeeRates();
    // Refetch when the app/tab regains focus so config changes propagate
    // without requiring a full page reload.
    const onFocus = () => refreshFeeRates();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') refreshFeeRates();
    });
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshFeeRates]);

  // Helper functions for fee calculations
  const calculateClientTotal = (serviceAmount) => {
    const fee = serviceAmount * (feeRates.client_fee_rate / 100);
    return {
      serviceAmount,
      clientFee: Math.round(fee * 100) / 100,
      clientTotal: Math.round((serviceAmount + fee) * 100) / 100,
      clientFeeRate: feeRates.client_fee_rate
    };
  };

  const calculateProviderPayout = (serviceAmount) => {
    const fee = serviceAmount * (feeRates.provider_fee_rate / 100);
    return {
      serviceAmount,
      providerFee: Math.round(fee * 100) / 100,
      providerPayout: Math.round((serviceAmount - fee) * 100) / 100,
      providerFeeRate: feeRates.provider_fee_rate
    };
  };

  // Pricing policy: a booking that is NOT yet paid is priced with the CURRENT
  // (live) platform fee, so Owner-Panel fee changes apply to unpaid bookings.
  // Once paid, the booking's stored breakdown is the locked rate and is used as-is.
  const getBookingPricing = (booking) => {
    const serviceAmount = Number(booking?.final_amount || booking?.price || 0);
    const isPaid = booking?.payment_status === 'paid';
    if (isPaid && booking?.client_fee != null) {
      const storedClientFee = booking.client_fee != null ? Number(booking.client_fee) : null;
      const storedProviderFee = booking.provider_fee != null
        ? Number(booking.provider_fee)
        : (booking.platform_fee != null ? Number(booking.platform_fee) : null);
      const clientTotal = Number(booking.client_total != null ? booking.client_total : serviceAmount + (storedClientFee || 0));
      const providerPayout = Number(booking.provider_payout != null ? booking.provider_payout : serviceAmount - (storedProviderFee || 0));
      // Some legacy/job bookings stored provider_payout/client_total but left
      // provider_fee/client_fee at 0. Derive the fee from the authoritative
      // total/payout so the displayed fee stays consistent with earnings.
      const clientFee = storedClientFee && storedClientFee > 0
        ? storedClientFee
        : Math.round((clientTotal - serviceAmount) * 100) / 100;
      const providerFee = storedProviderFee && storedProviderFee > 0
        ? storedProviderFee
        : Math.round((serviceAmount - providerPayout) * 100) / 100;
      return {
        serviceAmount,
        clientFee,
        clientFeeRate: booking.client_fee_rate != null ? Number(booking.client_fee_rate) : feeRates.client_fee_rate,
        providerFee,
        providerFeeRate: booking.provider_fee_rate != null
          ? Number(booking.provider_fee_rate)
          : (booking.platform_fee_rate != null ? Number(booking.platform_fee_rate) : feeRates.provider_fee_rate),
        clientTotal,
        providerPayout,
        locked: true,
      };
    }
    const clientFee = Math.round(serviceAmount * (feeRates.client_fee_rate / 100) * 100) / 100;
    const providerFee = Math.round(serviceAmount * (feeRates.provider_fee_rate / 100) * 100) / 100;
    return {
      serviceAmount,
      clientFee,
      clientFeeRate: feeRates.client_fee_rate,
      providerFee,
      providerFeeRate: feeRates.provider_fee_rate,
      clientTotal: Math.round((serviceAmount + clientFee) * 100) / 100,
      providerPayout: Math.round((serviceAmount - providerFee) * 100) / 100,
      locked: false,
    };
  };

  return (
    <FeeContext.Provider value={{ 
      feeRates, 
      refreshFeeRates,
      calculateClientTotal, 
      calculateProviderPayout,
      getBookingPricing
    }}>
      {children}
    </FeeContext.Provider>
  );
};

export const useFees = () => {
  const context = useContext(FeeContext);
  if (!context) {
    throw new Error('useFees must be used within a FeeProvider');
  }
  return context;
};

export default FeeContext;
